import { CommonModule, CurrencyPipe, DatePipe, registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr-CA';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { SharedLibraryModule } from "@cms/shared-library";
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { LoggedinUserNameComponent } from './components/loggedin-user-name/loggedin-user-name.component';
import { NotificationsItemComponent } from "./components/notifications-item/notifications-item.component";
import { NotificationsSearchComponent } from './components/notifications-search/notifications-search.component';
import { HttpRequestInterceptor } from './interceptor/http-request.interceptor';
import { AddressService } from './services/address/address.service';
import { AuthService } from './services/auth.service/auth.service';
import { CourseService } from './services/course/course.service';
import { DeficienciesCalculatorService } from './services/deficiencies-calculator/deficiencies-calculator.service';
import { DocumentService } from './services/document/document-service.service';
import { ErrorHandlerService } from './services/error-handle/error-handle.service';
import { FeedbackService } from './services/feedback/feedback.service';
import { IdleTimeoutService } from './services/idle-timeout-service/idle-timeout.service';
import { LookupObjectService } from './services/lookup-object/lookup-object.service';
import { NotesService } from './services/notes/notes.service';
import { NotificationService } from './services/notification/notification.service';
import { PageContentService } from './services/page-content/page-content.service';
import { PreFieldChecklistService } from './services/pre-field-checklist/pre-field-checklist.service';
import { StatusService } from './services/status/status.service';
import { TrainingEligibilityService } from './services/training-eligibility/training-eligibility.service';
import { TrainingProgramService } from './services/training-program/training-program.service';
import { TrainingProviderService } from './services/training-provider/training-provider.service';
import { TrainingTypeService } from './services/training-type/training-type.service';
import { UserService } from './services/uppy/user.service';
import { UtilsService } from './services/utils/utils.service';
import { WorkerService } from './services/worker-service/worker.service';
import { LocalizationModule } from './translation/localization.module';
import { CourseScheduleSearchResultCardComponent } from "./components/course-schedule-search-result-card/course-schedule-search-result-card.component";
import { CourseScheduleTileComponent } from "./components/course-schedule-tile/course-schedule-tile.component";
import { ScheduleService } from './services/field-visit/schedule.service';
import { GoogleMapsService } from './services/google-maps/google-maps.service';
import { MatBadgeModule } from '@angular/material/badge';
import { ExceptionLoggingInterceptor } from './interceptor/exception-logging.interceptor';
import { LoggingService } from './services/logging-service/logging-service.service';
import { PageContentLoopComponent } from './components/page-content-loop/page-content-loop.component';
import { GreyTextTileComponent } from './components/grey-text-tile/grey-text-tile.component';
import { BlackButtonComponent } from './components/black-button/black-button.component';
import { GenericParagraphComponent } from './components/generic-paragraph/generic-paragraph.component';
import { DynamicTabListComponent } from './components/dynamic-tab-list/dynamic-tab-list.component';
import { DynamicTabComponent } from './components/dynamic-tab/dynamic-tab.component';
import { LinkButtonComponent } from './components/link-button/link-button.component';
import { GreyTextBoxComponent } from './components/grey-text-box/grey-text-box.component';
import { PredictiveAddressSearchComponent } from './components/predictive-address-search/predictive-address-search.component';
import { MainSidebarComponent } from './components/main-sidebar/main-sidebar.component';
import { RouterModule } from '@angular/router';
import { FaqSectionComponent } from './components/faq-section/faq-section.component';
import { FaqQuestionComponent } from './components/faq-question/faq-question.component';
import { EmployerService } from './services/employer/employer.service';
import { StepsBannerComponent } from './components/steps-banner/steps-banner.component';
import { SoseApplicationService } from './services/sose-application/sose-application.service';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { BusinessInformationComponent } from './components/business-information/business-information.component';
import { ApplicantContactInformationComponent } from './components/applicant-contact-information/applicant-contact-information.component';
import { AddTeamMemberComponent } from './components/add-team-member/add-team-member.component';

registerLocaleData(localeFr, 'fr-CA')
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/locales', '.json');
}

@NgModule({
  declarations: [
    MainSidebarComponent,
    PredictiveAddressSearchComponent,
    LoggedinUserNameComponent,
    NotificationsItemComponent,
    NotificationsSearchComponent,
    CourseScheduleSearchResultCardComponent,
    CourseScheduleTileComponent, PageContentLoopComponent, GreyTextTileComponent, BlackButtonComponent, GenericParagraphComponent,
    DynamicTabListComponent, DynamicTabComponent, LinkButtonComponent,
    GreyTextBoxComponent, FaqQuestionComponent, FaqSectionComponent, StepsBannerComponent, FileUploadComponent, BusinessInformationComponent, ApplicantContactInformationComponent,
    AddTeamMemberComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    SharedLibraryModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatBadgeModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatMomentDateModule,
    MatButtonToggleModule,
    NativeDateModule,
    MatDialogModule,
    MatMenuModule,
    NgxMaskDirective,
    NgxMaskPipe,
    MatFormFieldModule,
    MatTooltipModule,
    MatSnackBarModule,
    LocalizationModule.forRoot({ locale_id: 'en' }),
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
  ],
  exports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    SharedLibraryModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatBadgeModule,
    MatMenuModule,
    MatTableModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatMomentDateModule,
    MatButtonToggleModule,
    NativeDateModule,
    MatDialogModule,
    MatMenuModule,
    NgxMaskDirective,
    NgxMaskPipe,
    MatFormFieldModule,
    MatTooltipModule,
    MatSnackBarModule,
    MainSidebarComponent,
    PredictiveAddressSearchComponent,
    StepsBannerComponent,
    FileUploadComponent,
    BusinessInformationComponent,
    ApplicantContactInformationComponent,
    AddTeamMemberComponent,
    LoggedinUserNameComponent,
    NotificationsItemComponent,
    NotificationsSearchComponent,
    TranslateModule, PageContentLoopComponent, GreyTextTileComponent, BlackButtonComponent, GenericParagraphComponent,
    DynamicTabListComponent, DynamicTabComponent, LinkButtonComponent,
    GreyTextBoxComponent, FaqQuestionComponent, FaqSectionComponent,
    CourseScheduleSearchResultCardComponent,
    CourseScheduleTileComponent],
  providers: [
    { provide: 'CourseService', useClass: CourseService },
    { provide: 'NotificationService', useClass: NotificationService },
    { provide: 'TrainingProviderService', useClass: TrainingProviderService },
    { provide: 'TrainingProgramService', useClass: TrainingProgramService },
    { provide: 'TrainingTypeService', useClass: TrainingTypeService },
    { provide: 'TrainingEligibilityService', useClass: TrainingEligibilityService },
    { provide: 'AddressService', useClass: AddressService },
    { provide: 'FeedbackService', useClass: FeedbackService },
    { provide: 'LookupObjectService', useClass: LookupObjectService },
    { provide: 'PageContentService', useClass: PageContentService },
    { provide: 'DocumentService', useClass: DocumentService },
    { provide: 'NotesService', useClass: NotesService },
    { provide: 'DeficienciesCalculatorService', useClass: DeficienciesCalculatorService },
    { provide: 'PreFieldChecklistService', useClass: PreFieldChecklistService },
    { provide: 'StatusService', useClass: StatusService },
    { provide: 'WorkerService', useClass: WorkerService },
    { provide: 'EmployerService', useClass: EmployerService},
    { provide: 'SoseApplicationService', useClass: SoseApplicationService},
    { provide: 'UserService', useClass: UserService },
    { provide: 'ScheduleService', useClass: ScheduleService },
    { provide: 'AuthService', useClass: AuthService },
    { provide: 'UtilsService', useClass: UtilsService },
    { provide: 'IdleTimeoutService', useClass: IdleTimeoutService },
    { provide: 'GoogleMapsService', useClass: GoogleMapsService },
    { provide: 'LoggingService', useClass: LoggingService },
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ExceptionLoggingInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    CurrencyPipe,
    DatePipe,
    provideNgxMask(),
    provideZoneChangeDetection()
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class GlobalModule { }
